import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * LazyImage — defers loading an image until its container scrolls near the
 * viewport, using a single IntersectionObserver per instance, AND bounds the
 * number of previews fetched at once via a shared module-level slot scheduler.
 *
 * Why this exists (issue #925, hardened for #1306 §E): the observation Files grid
 * rendered an <img> for every frame at once. IntersectionObserver alone bounds the
 * FIRST paint to what's near the viewport, but a fast scroll — or the token-rotation
 * re-fetch storm — could still fire a screenful+ of preview requests simultaneously
 * through the single-process API. The shared scheduler caps concurrent image loads at
 * PREVIEW_MAX_INFLIGHT, and a load error (e.g. the server shedding previews with 503
 * under load) backs off and retries instead of hot-spinning.
 *
 * The `src` is applied reactively: once shown, changing the `src` prop (e.g. because a
 * preview PNG was genuinely rebuilt) re-acquires a slot and re-fetches. Pass a STABLE
 * React `key` from the caller (e.g. file_id), never a churning timestamp.
 */

// Shared across ALL LazyImage instances: at most PREVIEW_MAX_INFLIGHT image loads run
// concurrently; the rest queue (FIFO) and are granted a slot as others complete.
const PREVIEW_MAX_INFLIGHT = 6;
const MAX_RETRIES = 2;
let inflight = 0;
const waiters = [];

/** Grant a slot to `onGrant` — synchronously if one is free, else when one frees. */
function acquireImageSlot(onGrant) {
  if (inflight < PREVIEW_MAX_INFLIGHT) {
    inflight += 1;
    onGrant();
  } else {
    waiters.push(onGrant);
  }
}

/** Release a held slot: hand it directly to the next waiter, or free it. */
function releaseImageSlot() {
  const next = waiters.shift();
  if (next) {
    next(); // transfer the slot (inflight unchanged)
  } else {
    inflight = Math.max(0, inflight - 1);
  }
}

/** Test seam: reset the shared scheduler between tests. */
export function __resetImageQueueForTests() {
  inflight = 0;
  waiters.length = 0;
}

const LazyImage = ({
  src,
  alt = '',
  height = 140,
  onClick,
  onError,
  sx,
  placeholderSx,
  rootMargin = '200px',
}) => {
  const containerRef = useRef(null);
  const [shown, setShown] = useState(false);   // scrolled near the viewport
  const [ready, setReady] = useState(false);   // slot acquired → render real <img>
  const [failed, setFailed] = useState(false); // retries exhausted → give up
  const [cooling, setCooling] = useState(false); // backoff window after an error
  const attemptRef = useRef(0);
  const holdingRef = useRef(false);             // do we currently hold a slot?
  const mountedRef = useRef(true);
  const prevSrcRef = useRef(src);

  const releaseIfHolding = () => {
    if (holdingRef.current) {
      holdingRef.current = false;
      releaseImageSlot();
    }
  };

  // Release the slot on unmount so a held/queued slot is never leaked.
  useEffect(() => () => {
    mountedRef.current = false;
    releaseIfHolding();
  }, []);

  // Viewport observer: mark shown once, then stay shown (avoid re-fetch churn).
  useEffect(() => {
    if (shown) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shown, rootMargin]);

  // A genuine src change (rebuilt preview): drop the held slot and re-acquire.
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      releaseIfHolding();
      attemptRef.current = 0;
      setReady(false);
      setFailed(false);
      setCooling(false);
    }
  }, [src]);

  // Acquire a shared slot before rendering the real <img>. Grant may be synchronous
  // (a slot is free) so the image appears immediately, or deferred (queue full).
  useEffect(() => {
    if (!shown || !src || ready || failed || cooling) return undefined;
    let cancelledBeforeGrant = false;
    let granted = false;
    acquireImageSlot(() => {
      if (cancelledBeforeGrant || !mountedRef.current) {
        releaseImageSlot(); // slot granted to a gone/cancelled instance — free it
        return;
      }
      granted = true;
      holdingRef.current = true;
      setReady(true);
    });
    return () => {
      if (!granted) cancelledBeforeGrant = true;
    };
  }, [shown, src, ready, failed, cooling]);

  const handleLoad = () => {
    releaseIfHolding();
  };

  const handleError = (e) => {
    releaseIfHolding();
    setReady(false);
    if (attemptRef.current >= MAX_RETRIES) {
      setFailed(true);
      if (onError) onError(e);
      return;
    }
    // Back off (with jitter) before retrying — a 503 under load must not hot-spin.
    const delay = Math.min(4000, 300 * (2 ** attemptRef.current)) + Math.floor(Math.random() * 150);
    attemptRef.current += 1;
    setCooling(true);
    setTimeout(() => { if (mountedRef.current) setCooling(false); }, delay);
  };

  return (
    <Box
      ref={containerRef}
      onClick={onClick}
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.900',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        ...placeholderSx,
      }}
    >
      {ready && src && !failed ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          // Low-priority so previews don't jostle higher-priority XHR/fetch on the
          // shared HTTP/2 connection (#1174 follow-up). Lowercase for React 18.2.
          fetchpriority="low"
          onLoad={handleLoad}
          onError={handleError}
          sx={{ width: '100%', height, objectFit: 'cover', ...sx }}
        />
      ) : null}
    </Box>
  );
};

export default LazyImage;
